import {
  Box,
  Button,
  Stack,
  Typography,
  keyframes,
  styled,
} from '@mui/material';
import { ARENA_TONES } from 'src/components/arenaSnackbar/index.styles';
import UserAvatar from 'src/pages/skillBuilder/components/common/UserAvatar';

/**
 * An invitation from a person, not a system notice.
 *
 * Built on the same dark plate as `arenaToast` rather than on the product's
 * light card. Every other thing multiplayer says arrives on that navy surface,
 * so a white card here read as a browser notification that had wandered in —
 * and this is the one toast in the app that asks for a decision.
 *
 * It takes the arena's `nudge` accent, which is exactly what it is.
 */
const { accent: ACCENT, glow: GLOW } = ARENA_TONES.nudge;

const slideIn = keyframes`
  from { opacity: 0; transform: translateY(-14px) scale(.96); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

/** Width comes from `style`: a styled-prop would mint a class name per frame. */
export const ToastCountdownBar = styled('div')({
  position: 'absolute',
  bottom: 0,
  left: 0,
  height: 3,
  backgroundColor: ACCENT,
  transition: 'width 0.1s linear',
  opacity: 0.7,
});

export const ToastRoot = styled(Stack)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  gap: 11,
  width: '100%',
  maxWidth: 380,
  padding: '13px 15px 15px',
  borderRadius: 16,
  border: '1px solid rgba(255,255,255,.14)',
  background: 'linear-gradient(135deg, #12345C 0%, #0C2444 55%, #081A33 100%)',
  boxShadow: `0 16px 40px rgba(4,14,30,.55), 0 0 0 1px ${GLOW} inset`,
  animation: `${slideIn} .32s cubic-bezier(.2,.9,.3,1) both`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
  [theme.breakpoints.down('sm')]: { maxWidth: '100%' },
}));

/**
 * Avatar beside the text, but only the text.
 *
 * The actions used to live inside this column, so their row started at the
 * text's left edge and "Mute" sat in a gutter of its own making. Actions belong
 * to the card, not to the sentence, so they get their own full-width row below.
 */
export const ToastIdentity = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  gap: 11,
});

export const ToastAvatar = styled(UserAvatar)({
  width: 40,
  height: 40,
  fontSize: 16,
  border: '2px solid rgba(255,255,255,.22)',
  boxShadow: 'none',
});

export const ToastBody = styled(Box)({ flex: 1, minWidth: 0 });

/** The inviter is the headline now: a person is asking, not a system. */
export const ToastName = styled(Typography)(({ theme }) => ({
  fontFamily: theme.palette.guidedCourse.fontDisplay,
  fontSize: 16,
  fontWeight: 800,
  lineHeight: 1.2,
  color: '#fff',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

/** Name and, for an AI player, its tag: a persona is labelled wherever it appears. */
export const ToastNameRow = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  gap: 7,
  minWidth: 0,
});

/** The arena's AI badge (`ArenaAiBadge`), sized for the card. */
export const ToastAiTag = styled(Box)(({ theme }) => ({
  flexShrink: 0,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '2px 7px',
  borderRadius: 999,
  fontFamily: theme.palette.guidedCourse.fontMono,
  fontSize: 9.5,
  fontWeight: 700,
  letterSpacing: '.14em',
  textTransform: 'uppercase',
  background: 'rgba(124,92,255,.22)',
  border: '1px solid rgba(167,139,250,.60)',
  color: '#c4b5fd',
  '& svg': { fontSize: 11 },
}));

/**
 * The ask, with the subject inside it.
 *
 * The skill was a chip on its own row, which made the card three rows tall to
 * say one sentence — and separated the topic from the only phrase that gives it
 * meaning. Inline and bold, it is the word the eye stops on.
 */
export const ToastSub = styled(Typography)(({ theme }) => ({
  marginTop: 2,
  fontFamily: theme.palette.guidedCourse.fontBody,
  fontSize: 13,
  fontWeight: 500,
  lineHeight: 1.35,
  color: 'rgba(255,255,255,.65)',
  '& b': { fontWeight: 800, color: '#fff' },
}));

/** Says what kind of toast this is before the eye reaches the buttons. */
export const ToastEyebrow = styled(Typography)(({ theme }) => ({
  // Mono and wide-tracked, matching `ArenaLabel` — the arena's own voice.
  fontFamily: theme.palette.guidedCourse.fontMono,
  display: 'flex',
  alignItems: 'center',
  marginBottom: 4,
  fontSize: 9.5,
  fontWeight: 700,
  letterSpacing: '.18em',
  textTransform: 'uppercase',
  color: ACCENT,
  '& svg': { fontSize: 13 },
}));

/**
 * Its own row under the text, not a column beside it.
 *
 * Sitting alongside, the buttons took the width the title needed to stay
 * readable — the truncation above was really a layout problem. Below, both the
 * sentence and the targets get full width.
 */
export const ToastActions = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'flex-end',
  flexWrap: 'wrap',
  gap: 8,
  marginTop: 0,
  /**
   * Buttons keep their labels on one line and the row wraps instead.
   *
   * With three actions the flex row shrank each one until every label broke
   * mid-phrase — "Mute For 5 / Min", "Not / Now", "Join / Game" — which turns
   * three buttons into six ambiguous fragments. `capitalize` is also undone
   * here: the global contained-button override outranks a plain styled class,
   * and "Mute For 5 Min" reads like a title, not an action.
   */
  '& .MuiButton-root': {
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  '& .MuiButton-root.MuiButton-contained': { textTransform: 'none' },
});

export const ToastHint = styled(Typography)(({ theme }) => ({
  fontFamily: theme.palette.guidedCourse.fontBody,
  fontSize: 10.5,
  lineHeight: 1.3,
  color: 'rgba(255,255,255,.42)',
}));

export const ToastHintLink = styled('button')({
  border: 'none',
  background: 'none',
  padding: 0,
  font: 'inherit',
  cursor: 'pointer',
  fontWeight: 'inherit',
  color: 'rgba(255,255,255,.62)',
  textDecoration: 'underline',
  textUnderlineOffset: 2,
  '&:hover': { color: '#fff' },
});

/** The one answer the card is fishing for, so it carries the accent. */
export const JoinButton = styled(Button)({
  padding: '7px 18px',
  borderRadius: 999,
  textTransform: 'none',
  fontSize: 13,
  fontWeight: 800,
  boxShadow: 'none',
  color: '#0C2444',
  backgroundColor: ACCENT,
  '&:hover': { backgroundColor: '#FFD98A', boxShadow: 'none' },
  '&:focus-visible': { outline: '3px solid #FFE27A', outlineOffset: 2 },
});

/**
 * "Stop asking", pushed to the far edge of the row.
 *
 * It has to be reachable at the moment the invite annoys you — a setting you
 * have to go find is no use then — but it is not one of the two answers to the
 * question being asked, so it does not sit next to them or look like them.
 */
export const MuteButton = styled(Button)({
  marginRight: 'auto',
  // Its own padding is cancelled so the bell starts on the card's left rail
  // rather than a few pixels inside it.
  marginLeft: -8,
  '& .MuiButton-startIcon': { marginLeft: 0, marginRight: 6 },
  '& svg': { fontSize: '16px !important' },
  padding: '6px 10px',
  minWidth: 'auto',
  borderRadius: 999,
  textTransform: 'none',
  fontSize: 11.5,
  fontWeight: 500,
  color: 'rgba(255,255,255,.62)',
  backgroundColor: 'transparent',
  boxShadow: 'none',
  '&:hover': {
    color: '#fff',
    backgroundColor: 'rgba(255,255,255,.12)',
    boxShadow: 'none',
  },
  '&.Mui-disabled': { color: 'rgba(255,255,255,.3)' },
});

/** Declining should be available, not persuasive. */
export const LaterButton = styled(Button)({
  padding: '7px 12px',
  minWidth: 'auto',
  borderRadius: 999,
  textTransform: 'none',
  fontSize: 12.5,
  fontWeight: 600,
  color: 'rgba(255,255,255,.62)',
  backgroundColor: 'transparent',
  boxShadow: 'none',
  '&:hover': {
    color: '#fff',
    backgroundColor: 'rgba(255,255,255,.12)',
    boxShadow: 'none',
  },
});
